/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { EXCHANGES } from "@/lib/constants";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {EquityHolding} from "@/integrations/supabase/equity.models.ts";

export default function EquityForm({
  holding,
  onSave,
  onCancel,
}: {
  holding?: EquityHolding;
  onSave: (data: any) => void;
  onCancel: () => void;
}) {
  const [symbol, setSymbol] = useState(holding?.symbol ?? "");
  const [companyName, setCompanyName] = useState(holding?.company_name ?? "");
  const [exchange, setExchange] = useState(holding?.exchange ?? "NYSE");
  const [sharesOwned, setSharesOwned] = useState(
    holding?.shares_owned?.toString() ?? "0",
  );
  const [purchasePrice, setPurchasePrice] = useState(
    holding?.purchase_price?.toString() ?? "0",
  );
  const [currentPrice, setCurrentPrice] = useState(
    holding?.current_price?.toString() ?? "0",
  );
  const [currency, setCurrency] = useState(holding?.currency ?? "USD");
  const [purchaseDate, setPurchaseDate] = useState(
    holding?.purchase_date ?? new Date().toISOString().split("T")[0],
  );
  const [notes, setNotes] = useState(holding?.notes ?? "");
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol.trim() || !companyName.trim()) return;
    onSave({
      symbol: symbol.trim().toUpperCase(),
      company_name: companyName.trim(),
      exchange,
      shares_owned: parseFloat(sharesOwned) || 0,
      purchase_price: parseFloat(purchasePrice) || 0,
      current_price: parseFloat(currentPrice) || 0,
      currency,
      purchase_date: purchaseDate,
      notes: notes.trim() || null,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label={holding ? "Edit equity holding" : "Add equity holding"}>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="equity-symbol">Stock Symbol *</Label>
          <Input
            id="equity-symbol"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="e.g. AAPL"
            required
            aria-required="true"
            aria-label="Stock symbol"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="equity-company-name">Company Name *</Label>
          <Input
            id="equity-company-name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="e.g. Apple Inc."
            required
            aria-required="true"
            aria-label="Company name"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="equity-exchange">Exchange</Label>
          <Select value={exchange} onValueChange={setExchange}>
            <SelectTrigger id="equity-exchange" aria-label="Select exchange">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXCHANGES.map((e) => (
                <SelectItem key={e} value={e}>
                  {e}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="equity-currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger id="equity-currency" aria-label="Select currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="equity-shares">Shares Owned</Label>
          <Input
            id="equity-shares"
            type="number"
            step="0.001"
            value={sharesOwned}
            onChange={(e) => setSharesOwned(e.target.value)}
            aria-label="Number of shares owned"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="equity-purchase-price">
            Purchase Price (per share)
          </Label>
          <Input
            id="equity-purchase-price"
            type="number"
            step="0.01"
            value={purchasePrice}
            onChange={(e) => setPurchasePrice(e.target.value)}
            aria-label="Purchase price per share"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="equity-current-price">
            Current Price (per share)
          </Label>
          <Input
            id="equity-current-price"
            type="number"
            step="0.01"
            value={currentPrice}
            onChange={(e) => setCurrentPrice(e.target.value)}
            aria-label="Current price per share"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="equity-purchase-date">Purchase Date</Label>
        <Input
          id="equity-purchase-date"
          type="date"
          value={purchaseDate}
          onChange={(e) => setPurchaseDate(e.target.value)}
          aria-label="Purchase date"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="equity-notes">Notes</Label>
        <Input
          id="equity-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          aria-label="Optional notes"
        />
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          aria-label="Cancel">
          Cancel
        </Button>
        <Button
          type="submit"
          aria-label={holding ? "Update holding" : "Add holding"}>
          {holding ? "Update" : "Add Holding"}
        </Button>
      </DialogFooter>
    </form>
  );
}
